import { ConfigMenuCategory } from 'root/navigation/navTypes';
import { AccountStage } from '@provider-portal/common/services/types/account';
import { NavBarMenuItem } from './NavBarMenu';

export const DRAWER_WIDTH = 211;

export const MENU_EDITOR_CATEGORY_ID = 'menu-editor';
export const MENU_EDITOR_SUMMARY_ITEM_ID = 'org-summary';
export const SETTINGS_CATEGORY_ID = 'settings';

/**
 * Determines if a menu category should be disabled based on account stage and menu editor settings
 */
export function isMenuCategoryDisabled(
  accountStage: AccountStage,
  menuCategory: ConfigMenuCategory,
  shouldEnableMerchantMenuEditor: boolean,
  isMenuEditorRoute: boolean,
): boolean {
  if (accountStage !== AccountStage.ONBOARDING) {
    return false;
  }

  // Menu editor category logic
  if (menuCategory.id === MENU_EDITOR_CATEGORY_ID && isMenuEditorRoute) {
    return !shouldEnableMerchantMenuEditor;
  }

  // Settings category is always enabled
  if (menuCategory.id === SETTINGS_CATEGORY_ID) {
    return false;
  }

  // All other categories are disabled during onboarding
  return true;
}

/**
 * Pins the menu editor category to the top and hides the summary item
 */
export function pinMenuEditorCategoryAndRemoveSummary(
  categories: ConfigMenuCategory[],
): ConfigMenuCategory[] {
  const categoryIndex = categories.findIndex((cat) => cat.id === MENU_EDITOR_CATEGORY_ID);

  if (categoryIndex > 0) {
    const [category] = categories.splice(categoryIndex, 1);

    // Hide summary item if it exists
    const summaryItem = category.items.find((item) => item.id === MENU_EDITOR_SUMMARY_ITEM_ID);
    if (summaryItem) {
      summaryItem.hidden = true;
    }

    categories.unshift(category);
  }

  return categories;
}

/**
 * Flattens menu categories into a sorted list of menu items
 */
export function flattenAndSortMenuItems(
  menuCategories: ConfigMenuCategory[],
  accountStage: AccountStage,
  shouldEnableMerchantMenuEditor: boolean,
  isMenuEditorRoute: boolean,
): NavBarMenuItem[] {
  const flattenedItems = menuCategories.reduce<NavBarMenuItem[]>((items, menuCategory) => {
    const shouldDisable = isMenuCategoryDisabled(
      accountStage,
      menuCategory,
      shouldEnableMerchantMenuEditor,
      isMenuEditorRoute,
    );

    const categoryItems = shouldDisable
      ? menuCategory.items.map((item) => ({ ...item, disabled: true }))
      : menuCategory.items;

    return [...items, ...categoryItems];
  }, []);

  return flattenedItems.sort((a, b) => {
    const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });
}

/**
 * Finds module name from menu category list by matching pathname
 */
export function getModuleNameFromMenuCategoryList(
  menuCategoryList: NavBarMenuItem[],
  pathname: string,
): string | null {
  const module = menuCategoryList.find((menuCategory) => menuCategory.href === pathname);
  return module?.id ?? null;
}
